import { useEffect, useRef, useState } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import { useIDPProviders } from '../../hooks/useIDPProviders'
import { ThemeToggle } from '../ThemeToggle/ThemeToggle'
import type { IDPProvider } from '../../types/auth'
import styles from './LoginActions.module.css'

export function LoginActions() {
  const { providers, loading, error, handleProviderLogin } = useIDPProviders()
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  const handleLoginClick = () => {
    if (providers.length === 1) {
      handleProviderLogin(providers[0])
    } else if (providers.length > 1) {
      setShowDropdown((prev) => !prev)
    }
  }

  const handleProviderSelect = (provider: IDPProvider) => {
    setShowDropdown(false)
    handleProviderLogin(provider)
  }

  return (
    <>
      <div className={styles.topActions} ref={dropdownRef}>
        <ThemeToggle />
        <div className={styles.loginButtonWrapper}>
          <button
            type="button"
            className={styles.loginButton}
            onClick={handleLoginClick}
            disabled={loading || (!loading && providers.length === 0)}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Login'}
          </button>

          {!loading && providers.length === 0 && (
            <p className={styles.errorText} style={{ margin: '8px 0 0', maxWidth: 280 }}>
              No sign-in providers are configured. Enable Azure AD for the organization in Admin, then restart Auth Service.
            </p>
          )}

          {showDropdown && providers.length > 1 && (
            <div className={styles.providerDropdown}>
              {providers.map((provider) => (
                <button
                  key={provider.provider_key}
                  type="button"
                  className={styles.providerOption}
                  onClick={() => handleProviderSelect(provider)}
                >
                  {provider.button_icon_url ? (
                    <img src={provider.button_icon_url} alt="" className={styles.providerIcon} />
                  ) : (
                    <ShieldCheck size={20} />
                  )}
                  <span>{provider.button_label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorToast}>
          <span className={styles.errorText}>{error}</span>
          <button type="button" className={styles.retryButton} onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      )}
    </>
  )
}
